import { useState, useCallback, useEffect } from 'react';
import { api } from '../lib/api';
import type { Site, CreateSiteDto, UpdateSiteDto } from '../types';

export function useSites() {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSites();
      setSites(data);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Помилка завантаження сайтів';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const createSite = useCallback(async (data: CreateSiteDto): Promise<Site> => {
    const newSite = await api.createSite(data);
    setSites((prev) => [...prev, newSite]);
    return newSite;
  }, []);

  const updateSite = useCallback(async (id: string, data: UpdateSiteDto): Promise<Site> => {
    const updatedSite = await api.updateSite(id, data);
    setSites((prev) => prev.map((s) => (s.id === id ? updatedSite : s)));
    return updatedSite;
  }, []);

  const deleteSite = useCallback(async (id: string): Promise<void> => {
    await api.deleteSite(id);
    setSites((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const regenerateKeys = useCallback(async (id: string): Promise<Site> => {
    const updatedSite = await api.regenerateKeys(id);
    setSites((prev) => prev.map((s) => (s.id === id ? updatedSite : s)));
    return updatedSite;
  }, []);

  return {
    sites,
    loading,
    error,
    fetchSites,
    createSite,
    updateSite,
    deleteSite,
    regenerateKeys,
  };
}
